import React, { useRef, useState, MutableRefObject } from 'react'
import { IoIosCloudUpload } from 'react-icons/io'

export const RECEIPT_VAULT_FILE_ACCEPT = '.png,.jpg,.jpeg,.heic,.heif,.pdf,.msg,.eml'
export const RECEIPT_VAULT_SUPPORTED_LABEL = 'Supported: PNG, JPG, JPEG, HEIC, PDF, MSG, EML'

type Props = {
    onFilesSelected: (files: File[]) => void
    inputRef?: MutableRefObject<HTMLInputElement | null>
    multiple?: boolean
    accept?: string
    className?: string
    disabled?: boolean
    onActivate?: () => void
}

const ReceiptVaultFileDropzone = ({
    onFilesSelected,
    inputRef,
    multiple = true,
    accept = RECEIPT_VAULT_FILE_ACCEPT,
    className = '',
    disabled = false,
    onActivate,
}: Props) => {
    const internalRef = useRef<HTMLInputElement>(null)
    const fileInputRef = inputRef ?? internalRef
    const [dragActive, setDragActive] = useState(false)

    const emitFiles = (fileList: FileList | File[] | null) => {
        if (disabled || !fileList?.length) return
        onFilesSelected(Array.from(fileList))
    }

    const openFilePicker = () => {
        if (disabled) return
        onActivate?.()
        fileInputRef.current?.click()
    }

    const handleDrag = (event: React.DragEvent<HTMLDivElement>, active: boolean) => {
        event.preventDefault()
        event.stopPropagation()
        if (!disabled) setDragActive(active)
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        setDragActive(false)
        if (disabled) return
        onActivate?.()
        emitFiles(event.dataTransfer.files)
    }

    return (
        <div
            className={`position-relative ss-rv-file-dropzone ${dragActive ? 'ss-rv-file-dropzone--active' : ''} ${disabled ? 'ss-rv-file-dropzone--disabled' : ''} ${className}`.trim()}
            onClick={openFilePicker}
            onDragEnter={(event) => handleDrag(event, true)}
            onDragOver={(event) => handleDrag(event, true)}
            onDragLeave={(event) => handleDrag(event, false)}
            onDrop={handleDrop}
            role='button'
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openFilePicker()
                }
            }}
            aria-label='Drag and drop files here or click to browse'
            aria-disabled={disabled}
        >
            <div className='ss-rv-file-dropzone__surface'>
                <IoIosCloudUpload className='ss-rv-file-dropzone__icon' aria-hidden='true' />
                <h3 className='ss-rv-file-dropzone__title'>Drag &amp; Drop Images / Files Here</h3>
                <p className='ss-rv-file-dropzone__hint mb-0'>{RECEIPT_VAULT_SUPPORTED_LABEL}</p>
            </div>
            <input
                type='file'
                ref={(node) => {
                    ;(fileInputRef as React.MutableRefObject<HTMLInputElement | null>).current = node
                }}
                accept={accept}
                multiple={multiple}
                disabled={disabled}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => {
                    emitFiles(event.target.files)
                    event.target.value = ''
                }}
                className='ss-rv-file-dropzone__input'
                tabIndex={-1}
                aria-hidden='true'
            />
        </div>
    )
}

export default ReceiptVaultFileDropzone
